import { zodResolver } from '@hookform/resolvers/zod'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'

const FillEyeIcon = AiFillEye as React.ElementType
const FillEyeInvisibleIcon = AiFillEyeInvisible as React.ElementType
import { useForgotPassword, useLogin } from './api'
import { LoginSchema, loginSchema } from './schema'
import { useNavigate } from 'react-router-dom'

import { z } from 'zod'
const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email'),
})
type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>

export default function AdminLogin() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)

  const { mutate } = useLogin(() => navigate('/dashboard'))
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  })

  const {
    formState: { errors: forgotErrors },
    handleSubmit: handleForgotSubmit,
    register: registerForgot,
  } = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })
  const { mutate: forgotPasswordMutate } = useForgotPassword(() => {
    setShowForgotPassword(false)
  })
  const handleLogin = (data: LoginSchema) => {
    const formData = new FormData()
    formData.append('email', data.email)
    formData.append('password', data.password)
    mutate(formData)
  }

  const handleForgotPassword = (data: ForgotPasswordSchema) => {
    forgotPasswordMutate({ email: data.email })
  }
  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-white px-4">
      {!showForgotPassword ? (
        <form
          onSubmit={handleSubmit(handleLogin)}
          className="bg-white border border-gray-200 p-8 rounded-2xl shadow-sm w-full max-w-xl"
        >
          <h2 className="text-3xl font-bold text-center text-black mb-2">
            Welcome Back
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            Login to manage your account and explore new updates.
          </p>

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Email Address
            </label>
            <input
              type="email"
              placeholder="Email address"
              {...register('email')}
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 placeholder-gray-400"
            />
            {errors?.email && (
              <p className="text-xs text-red-500 mt-1">
                {errors.email.message as string}
              </p>
            )}
          </div>

          <div className="mb-2">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                {...register('password')}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 placeholder-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showPassword ? (
                  <FillEyeIcon className="h-5 w-5" />
                ) : (
                  <FillEyeInvisibleIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors?.password && (
              <p className="text-xs text-red-500 mt-1">
                {errors.password.message as string}
              </p>
            )}
          </div>

          <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
            <label className="flex items-center space-x-2">
              <input type="checkbox" className="accent-orange-400" />
              <span>Remember me</span>
            </label>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-orange-500 hover:underline"
            >
              Forgot your password?
            </button>
          </div>

          <button
            type="submit"
            className="w-full bg-orange-400 hover:bg-white0 transition text-white font-semibold text-sm py-2 rounded-md"
          >
            Log In
          </button>
        </form>
      ) : (
        <form
          onSubmit={handleForgotSubmit(handleForgotPassword)}
          className="bg-white border border-gray-200 p-8 rounded-2xl shadow-sm w-full max-w-md"
        >
          <h2 className="text-2xl font-bold text-center text-black mb-2">
            Forgot Password
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            Enter your email address to reset your password.
          </p>

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Email Address
            </label>
            <input
              type="email"
              placeholder="Email address"
              {...registerForgot('email')}
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 placeholder-gray-400"
            />
            {forgotErrors?.email && (
              <p className="text-xs text-red-500 mt-1">
                {forgotErrors.email.message as string}
              </p>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              className="flex-1 bg-orange-400 hover:bg-white0 transition text-white font-semibold text-sm py-2 rounded-md"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="flex-1 bg-gray-200 hover:bg-gray-300 transition text-gray-700 font-semibold text-sm py-2 rounded-md"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
